import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@yeshuman/sdk'
import { Lock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { usePrivacySettings, useUpdatePrivacySettings } from '../../hooks/use-privacy-settings'
import { ProfileVisibilityExplainer } from './candidate-privacy-page'

/**
 * Shown on the candidate home until dismissed, so new candidates learn that a
 * profile page exists, who can see it, and where to change that.
 */
export function ProfileVisibilityNotice({ className }: { className?: string }) {
  const navigate = useNavigate()
  const { data } = usePrivacySettings()
  const update = useUpdatePrivacySettings()

  if (!data || data.notice_acknowledged) return null

  const isPublic = data.profile_visibility === 'public'

  return (
    <Card className={className} data-testid="profile-visibility-notice">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="size-4" aria-hidden />
          Who can see your profile
        </CardTitle>
        <CardDescription alwaysShow>
          {isPublic
            ? 'Anyone with your profile link can see it.'
            : 'Your profile is visible to signed-in employers only. It is not public.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ProfileVisibilityExplainer />
        <p className="text-sm text-muted-foreground">
          You can share a public link if you want to, and change this at any time in Privacy.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { acknowledge_notice: true },
                { onError: (error) => toast.error(error.message) },
              )
            }
          >
            Got it
          </Button>
          <Button onClick={() => navigate('/candidate/privacy')}>Privacy settings</Button>
        </div>
      </CardContent>
    </Card>
  )
}
